import { Fragment, useEffect, useRef } from 'react'
import cn from 'classnames'
import { ConfirmedSegment, gapBefore, isPause } from '../../../shared/labs/transcript'
import { KeepTalkingModal } from '../../../shared/components/KeepTalkingModal'
import { RebusPicture } from './RebusPicture'

// The persistent rebus play surface: the picture puzzle and the running transcript. Used for
// the whole post-commit journey too (reflection → reveal). Mirrors the cryptic/24 stages,
// including the "hide until you talk" mechanic — when energy is depleted (no talking) the
// picture fades out behind a keep-talking prompt, so thinking aloud keeps it visible.
export const RebusStage = ({
  puzzleId,
  dimmed = false,
  confirmedSegments,
  interimText,
  progressLabel,
  showNudge = false,
  middle,
  children,
}: {
  puzzleId: string
  dimmed?: boolean
  confirmedSegments: ConfirmedSegment[]
  interimText: string
  progressLabel?: string
  showNudge?: boolean
  middle?: React.ReactNode
  children?: React.ReactNode
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [confirmedSegments, interimText, children])

  return (
    <div className="flex flex-col items-center px-6 pt-10 pb-4">
      <div className="w-full max-w-lg">
        {progressLabel && (
          <p className="text-center text-xs font-medium uppercase tracking-wide text-gray-400">{progressLabel}</p>
        )}
        <p className="mt-1 text-center text-gray-700">
          What does the <span className="font-semibold text-gray-900">picture</span> say? Think out loud as you work it out.
        </p>
        <p className="mt-1 text-center text-xs text-gray-400">
          A rebus spells a word or phrase from pictures, letters and their positions. Say “commit” when you’ve got it.
        </p>

        <div className="relative mt-6">
          <div className="relative flex min-h-[10rem] items-center justify-center rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            {/* Picture fades out and a keep-talking prompt fades in when energy is depleted. */}
            <RebusPicture puzzleId={puzzleId} dimmed={dimmed} />
            <span
              className={cn(
                'absolute inset-0 flex items-center justify-center px-6 text-center text-sm font-medium text-gray-500 transition-opacity duration-700',
                dimmed ? 'opacity-100' : 'pointer-events-none opacity-0'
              )}
            >
              Keep talking to reveal the picture…
            </span>
          </div>

          {showNudge && (
            <div className="pointer-events-none absolute inset-x-0 top-full z-40 translate-y-2/3 flex justify-center px-2">
              <KeepTalkingModal />
            </div>
          )}
        </div>

        {middle && <div className="mt-6">{middle}</div>}

        <div className="mt-4 text-xs text-gray-400">What we’re hearing</div>
        <div className="relative mt-1">
          <div ref={scrollRef} className="h-40 overflow-y-auto rounded-lg bg-gray-50 px-3 py-2 text-sm leading-relaxed">
            {confirmedSegments.length === 0 && !interimText && (
              <span className="text-gray-400 italic">Start talking through your idea…</span>
            )}
            {confirmedSegments.map((s, i) => {
              const gap = gapBefore(confirmedSegments, i)
              return (
                <Fragment key={i}>
                  {i > 0 &&
                    (isPause(gap) ? (
                      <span className="mx-1 text-xs text-gray-400 select-none whitespace-nowrap align-middle">
                        {'<'}
                        {(gap / 1000).toFixed(1)}s{'>'}
                      </span>
                    ) : (
                      ' '
                    ))}
                  <span className="text-gray-700">{s.text}</span>
                </Fragment>
              )
            })}
            {interimText && <span className="text-gray-400 italic"> {interimText}</span>}
          </div>
        </div>

        {children && <div className="mt-5 pb-10">{children}</div>}
      </div>
    </div>
  )
}
